import React, { useEffect } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import AACChromeAPI, { ChromeEvent } from '@amzn/aac-chrome-api';
import { UserInfo } from '@amzn/aac-chrome-internal-api';
import { FocusPageHeader } from '@amzn/storm-ui-page-header';
import { RootState } from 'src/app/store';
import { Flag } from 'src/app/slice';
import { selectUserInfoFromHeader } from 'src/features/header/store/slice';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { Actions } from '../PageHeader/Actions/Actions';
import { selectRenderTagInPageName } from 'src/features/nav/store/slice';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { formatFlagType } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { LaunchFlag } from 'src/commons/components/LaunchFlag/LaunchFlag';
import FocusHeaderAdsAgentButton, {
  useShowFocusHeaderAdsAgentToggle,
} from './FocusHeaderAdsAgentButton';

const FocusHeaderRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`;

/**
 * Inline-start slot for the Ads Agent chat toggle, sitting BEFORE the
 * FocusPageHeader (whose close button is its first child), so the toggle
 * lands at the very start of the bar next to where the embedded chat panel
 * slides out. Mirrors PageHeaderWrapper's background and bottom border so
 * the slot reads as part of the same bar.
 */
const AdsAgentSlot = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.pageHeader.backgroundColor};
  border-block-end: ${({ theme }) => theme.border.subtle.default};
  box-sizing: border-box;
`;

const PageHeaderGrow = styled.div<{ tightenStart: boolean }>`
  flex: 1;
  min-width: 0;

  /* Tuck the close button up against the Ads Agent separator: the focus
     bar's own inline-start padding ('small base') normally offsets the
     close button from the bar edge, but the Ads Agent slot + separator
     occupy that space when the toggle renders (per the Ads Agent console
     mock). Applied ONLY alongside the toggle so focus-mode pages of other
     applications keep the stock header layout untouched. Visual-only
     override of the storm-ui-page-header wrapper padding. */
  ${({ tightenStart, theme }) =>
    tightenStart &&
    `
    && > header {
      padding-inline-start: ${theme.spacing.xxsmall.default};
    }
  `}
`;

export const FocusHeader: React.VFC<{
  pageTitleTag?: Flag | null;
  userInfo: UserInfo;
}> = ({ pageTitleTag, userInfo }) => {
  useEffect(() => {
    didElementLoad('focusNav');
  }, []);
  const chromeConfig = useChromeConfigContext();
  const showAdsAgentToggle = useShowFocusHeaderAdsAgentToggle();
  const pageName = chromeConfig.pageHeader?.pageName;
  const renderTitle = chromeConfig.pageHeader?.getRenderTitle?.();
  const objectInfo = chromeConfig.pageHeader?.objectInfo;
  const actions = objectInfo?.actions?.list;
  const errorMessage = objectInfo?.actionErrorMessage;

  const handleCloseButtonClick = (event) => {
    if (typeof document !== 'undefined') {
      const eventTarget = AACChromeAPI.getCommonEventTarget();
      const chromeEvent = new ChromeEvent('ExitFocusView');
      chromeEvent.detail = {
        originEvent: event,
      };
      eventTarget.dispatchEvent(chromeEvent);
    }
  };

  return (
    <FocusHeaderRow>
      {/* Chrome-owned Ads Agent chat toggle (UCB weblab + /cb pages only).
          Rendered OUTSIDE the config-driven actions list on purpose: page
          frameworks (e.g. AtlasFormRenderer) replace pageHeader.objectInfo
          wholesale via the chromeConfig API and would wipe any button
          registered there. When hidden, the header renders exactly as
          before — no slot, no padding change. */}
      {showAdsAgentToggle && (
        <AdsAgentSlot>
          <FocusHeaderAdsAgentButton />
        </AdsAgentSlot>
      )}
      <PageHeaderGrow tightenStart={showAdsAgentToggle}>
        <FocusPageHeader
          id="focusNav"
          applicationName={chromeConfig.appName}
          onClose={handleCloseButtonClick}
          title={renderTitle ?? pageName}
          renderActions={() => <Actions actions={actions} />}
          actionsErrorMessage={errorMessage}
          renderPageTitleIcon={() => (
            <>
              {pageTitleTag &&
                (() => {
                  const flagType = formatFlagType(pageTitleTag.type);

                  return (
                    <LaunchFlag
                      flag={{
                        type:
                          flagType !== 'beta_ai_with_icon'
                            ? flagType
                            : undefined,
                        label: pageTitleTag.label,
                        description:
                          pageTitleTag.description ?? pageTitleTag.label,
                      }}
                    />
                  );
                })()}
            </>
          )}
          locale={userInfo.languageInfo.currentLocale}
        />
      </PageHeaderGrow>
    </FocusHeaderRow>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    pageTitleTag: selectRenderTagInPageName(state),
    userInfo: selectUserInfoFromHeader(state),
  };
};
export const ConnectedFocusHeader = connect(mapStateToProps)(FocusHeader);
